import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  applyPlan,
  generateApp,
  type GenerateAppFailedResponse,
  type GenerateAppResponse,
  type PreviewNode,
} from './aiClient';
import { ModelPicker } from './ModelPicker';

/**
 * Generate App modal mounted on App Settings. Two-step flow:
 *   1. User types a prompt + picks a style preset → POST /ai/generate-app
 *      → server returns { planId, preview }.
 *   2. User reviews the preview tree → clicks Apply → POST /apply
 *      → server runs snapshot-and-swap and the new tree goes live.
 *
 * If the existing app has a main_view_id, Apply replaces it (server
 * snapshots before swap so a future Undo path is possible).
 */
export interface GenerateAppModalProps {
  open: boolean;
  onClose: () => void;
  /** Existing appId to replace into. Optional — undefined creates a new app. */
  appId?: string;
  /** True if the app already has a main_view_id (drives the warning banner). */
  hasExistingRoot: boolean;
}

const PRESETS: Array<{ id: string; label: string }> = [
  { id: 'minimal', label: 'Minimal' },
  { id: 'vibrant', label: 'Vibrant' },
  { id: 'pro', label: 'Professional' },
  { id: 'playful', label: 'Playful' },
];

const EXAMPLE_PROMPTS = [
  'A meditation app with daily reminders and a calm timer',
  'A fitness tracker with workouts, profile, and progress charts',
  'A note-taking app with folders, search, and a write screen',
  'A small e-commerce app with product list, detail, and cart',
];

export function GenerateAppModal({
  open,
  onClose,
  appId,
  hasExistingRoot,
}: GenerateAppModalProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [prompt, setPrompt] = useState('');
  const [preset, setPreset] = useState<string>('minimal');
  /**
   * Selected model slug. `undefined` = use server default (no `model`
   * sent on the request). User explicit picks become a string here
   * and are forwarded to /generate-app.
   */
  const [model, setModel] = useState<string | undefined>(undefined);
  const [generated, setGenerated] = useState<GenerateAppResponse | null>(null);
  const [failed, setFailed] = useState<GenerateAppFailedResponse | null>(null);

  const generate = useMutation({
    mutationFn: () => generateApp({ prompt, stylePreset: preset, appId, model }),
    onSuccess: (data) => {
      if ('ok' in data && data.ok === false) {
        setFailed(data);
        setGenerated(null);
      } else {
        setGenerated(data as GenerateAppResponse);
        setFailed(null);
      }
    },
  });

  const apply = useMutation({
    mutationFn: () => {
      if (!generated) throw new Error('No plan to apply');
      return applyPlan({ planId: generated.planId, appId });
    },
    onSuccess: (data) => {
      // Cache invalidation across all the views that depend on FrontendApp.
      queryClient.invalidateQueries({ queryKey: ['frontend-app', data.appId] });
      queryClient.invalidateQueries({ queryKey: ['app-data', data.appId] });
      queryClient.invalidateQueries({ queryKey: ['nav-views', data.appId] });
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      // For new-app generation (no appId passed in), navigate to the new app.
      if (!appId) navigate(`/apps/${data.appId}`);
      handleClose();
    },
  });

  const handleClose = () => {
    if (generate.isPending || apply.isPending) return;
    setPrompt('');
    setPreset('minimal');
    setModel(undefined);
    setGenerated(null);
    setFailed(null);
    generate.reset();
    apply.reset();
    onClose();
  };

  if (!open) return null;

  const previewing = generated !== null;

  return (
    <div style={s.backdrop} onClick={handleClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <header style={s.header}>
          <h2 style={s.title}>Generate App with AI</h2>
          <button type="button" style={s.closeBtn} onClick={handleClose}>×</button>
        </header>

        {!previewing && (
          <>
            <p style={s.hint}>
              Describe the app you want. Forge's AI will design a screen-by-screen
              plan you can review before committing.
            </p>

            <textarea
              style={s.prompt}
              placeholder="A meditation app with daily reminders…"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              disabled={generate.isPending}
            />

            <div style={s.examples}>
              {EXAMPLE_PROMPTS.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  style={s.exampleChip}
                  onClick={() => setPrompt(ex)}
                  disabled={generate.isPending}
                >
                  {ex}
                </button>
              ))}
            </div>

            <div style={s.section}>
              <h3 style={s.sectionTitle}>Style</h3>
              <div style={s.presetRow}>
                {PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPreset(p.id)}
                    style={preset === p.id ? s.presetChipActive : s.presetChip}
                    disabled={generate.isPending}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <ModelPicker
              flow="generate"
              value={model}
              onChange={setModel}
              variant="card"
              disabled={generate.isPending}
            />

            {hasExistingRoot && (
              <p style={s.warn}>
                <strong>Heads up:</strong> this app already has content. Applying
                a generated plan will replace the existing root subtree. The
                server snapshots the old version first, but the change is
                disruptive — back up anything important first.
              </p>
            )}

            {failed && (
              <div style={s.errorBox}>
                <p style={s.errorTitle}>{failed.message}</p>
                <ul style={s.errorList}>
                  {failed.errors.slice(0, 6).map((e, i) => (
                    <li key={i} style={s.errorItem}>{e.message}</li>
                  ))}
                </ul>
              </div>
            )}

            <div style={s.actions}>
              <button
                type="button"
                style={prompt.trim() ? s.primaryBtn : s.primaryBtnDisabled}
                onClick={() => generate.mutate()}
                disabled={!prompt.trim() || generate.isPending}
              >
                {generate.isPending ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </>
        )}

        {previewing && generated && (
          <>
            <p style={s.hint}>
              Here's the proposed app. Review the screens; click Apply to commit
              or Discard to start over.
            </p>

            {generated.salvaged && (
              <div style={s.salvagedBanner}>
                <strong>Salvaged plan:</strong> {generated.message ?? 'Some items had to be dropped to produce an applyable plan. See below.'}
              </div>
            )}

            <div style={s.previewWrapper}>
              {generated.preview ? (
                <PreviewTree node={generated.preview} depth={0} />
              ) : (
                <p style={s.empty}>(empty preview)</p>
              )}
            </div>

            {generated.dropped && generated.dropped.length > 0 && (
              <details style={s.droppedDetails} open>
                <summary style={s.droppedSummary}>
                  Dropped {generated.dropped.length} item(s)
                </summary>
                <ul style={s.droppedList}>
                  {generated.dropped.map((d, i) => (
                    <li key={i} style={s.droppedItem}>
                      <span style={s.droppedCategory}>[{d.category}]</span> {d.reason}
                      {d.path && <span style={s.droppedPath}> @ {d.path}</span>}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <p style={s.usage}>
              {generated.model && (
                <>
                  <strong style={s.usageModel}>{generated.model}</strong>
                  {generated.modelSource === 'override' && (
                    <span style={s.overrideTag}>override</span>
                  )}
                  {generated.cost && (
                    <span style={s.costTag}>
                      ${generated.cost.totalUsd.toFixed(4)}
                      {generated.cost.cacheSavingsUsd > 0 &&
                        ` (cache saved $${generated.cost.cacheSavingsUsd.toFixed(4)})`}
                    </span>
                  )}
                  {' · '}
                </>
              )}
              {generated.attempts > 1
                ? `${generated.attempts} attempts · `
                : ''}
              {generated.usage
                ? `${generated.usage.input_tokens} in + ${generated.usage.output_tokens} out tokens`
                : ''}
            </p>

            {apply.isError && (
              <p style={s.errorTitle}>
                Apply failed: {(apply.error as Error).message}
              </p>
            )}

            <div style={s.actions}>
              <button
                type="button"
                style={s.secondaryBtn}
                onClick={() => {
                  setGenerated(null);
                  setFailed(null);
                  generate.reset();
                }}
                disabled={apply.isPending}
              >
                Discard, try again
              </button>
              <button
                type="button"
                style={s.primaryBtn}
                onClick={() => apply.mutate()}
                disabled={apply.isPending}
              >
                {apply.isPending ? 'Applying…' : 'Apply'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PreviewTree({ node, depth }: { node: PreviewNode; depth: number }) {
  return (
    <div style={{ marginLeft: depth * 14 }}>
      <div style={s.previewRow}>
        <span style={s.previewType}>{node.type}</span>
        <span style={s.previewName}>{node.name}</span>
      </div>
      {node.children.map((c) => (
        <PreviewTree key={c.key} node={c} depth={depth + 1} />
      ))}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
  },
  modal: {
    width: 600, maxWidth: '95vw', maxHeight: '90vh',
    backgroundColor: '#fff', borderRadius: 10, padding: 22,
    overflowY: 'auto',
    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.25)',
    fontFamily: 'system-ui, sans-serif',
  },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  title: { margin: 0, fontSize: 16, fontWeight: 700, color: '#0F172A' },
  closeBtn: {
    width: 28, height: 28, padding: 0, fontSize: 20,
    color: '#64748B', backgroundColor: 'transparent',
    border: 'none', cursor: 'pointer',
  },
  hint: { margin: '0 0 14px', fontSize: 13, color: '#475569', lineHeight: 1.5 },
  prompt: {
    width: '100%', padding: 10, fontSize: 14, color: '#0F172A',
    border: '1px solid #CBD5E1', borderRadius: 6, fontFamily: 'inherit',
    resize: 'vertical', boxSizing: 'border-box',
  },
  examples: { display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' },
  exampleChip: {
    padding: '4px 9px', fontSize: 11, color: '#4F46E5',
    backgroundColor: '#EEF2FF', border: '1px solid #C7D2FE',
    borderRadius: 14, cursor: 'pointer',
  },
  section: { marginTop: 16 },
  sectionTitle: { margin: '0 0 6px', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  presetRow: { display: 'flex', gap: 6 },
  presetChip: {
    padding: '6px 12px', fontSize: 12, fontWeight: 500,
    color: '#64748B', backgroundColor: '#fff',
    border: '1px solid #CBD5E1', borderRadius: 6, cursor: 'pointer',
  },
  presetChipActive: {
    padding: '6px 12px', fontSize: 12, fontWeight: 600,
    color: '#fff', backgroundColor: '#4F46E5',
    border: '1px solid #4F46E5', borderRadius: 6, cursor: 'pointer',
  },
  warn: {
    marginTop: 14, padding: 10, fontSize: 12, color: '#92400E',
    backgroundColor: '#FEF3C7', borderRadius: 6, border: '1px solid #FCD34D',
  },
  errorBox: {
    marginTop: 14, padding: 10, backgroundColor: '#FEF2F2',
    borderRadius: 6, border: '1px solid #FECACA',
  },
  errorTitle: { margin: '0 0 4px', fontSize: 12, fontWeight: 600, color: '#B91C1C' },
  errorList: { margin: 0, paddingLeft: 16, fontSize: 11, color: '#7F1D1D' },
  errorItem: { marginBottom: 2 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  primaryBtn: {
    padding: '8px 16px', fontSize: 13, fontWeight: 600,
    color: '#fff', backgroundColor: '#4F46E5',
    border: 'none', borderRadius: 6, cursor: 'pointer',
  },
  primaryBtnDisabled: {
    padding: '8px 16px', fontSize: 13, fontWeight: 600,
    color: '#94A3B8', backgroundColor: '#E2E8F0',
    border: 'none', borderRadius: 6, cursor: 'not-allowed',
  },
  secondaryBtn: {
    padding: '8px 14px', fontSize: 13, fontWeight: 500,
    color: '#64748B', backgroundColor: 'transparent',
    border: '1px solid #CBD5E1', borderRadius: 6, cursor: 'pointer',
  },
  previewWrapper: {
    marginTop: 12, padding: 12, maxHeight: 320, overflowY: 'auto',
    border: '1px solid #E2E8F0', borderRadius: 6, backgroundColor: '#F8FAFC',
    fontSize: 12,
  },
  previewRow: { display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 0' },
  previewType: {
    fontSize: 9, fontWeight: 700, color: '#94A3B8',
    textTransform: 'uppercase' as const, letterSpacing: 0.5,
  },
  previewName: { fontSize: 12, color: '#0F172A' },
  empty: { color: '#94A3B8', fontSize: 12 },
  usage: { marginTop: 8, fontSize: 11, color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' },
  usageModel: { color: '#0F172A', fontWeight: 600, marginRight: 2 },
  overrideTag: {
    fontSize: 9, fontWeight: 700, color: '#92400E',
    backgroundColor: '#FEF3C7', padding: '1px 5px', borderRadius: 8,
    textTransform: 'uppercase' as const, letterSpacing: 0.4,
  },
  costTag: {
    fontSize: 10, fontWeight: 600, color: '#047857',
    backgroundColor: '#ECFDF5', padding: '1px 5px', borderRadius: 8,
    fontFamily: 'ui-monospace, monospace',
  },
  salvagedBanner: {
    marginTop: 10, marginBottom: 10, padding: 10, fontSize: 12,
    color: '#92400E', backgroundColor: '#FEF3C7',
    border: '1px solid #FCD34D', borderRadius: 6, lineHeight: 1.5,
  },
  droppedDetails: {
    marginTop: 10, padding: 10, backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA', borderRadius: 6,
  },
  droppedSummary: {
    fontSize: 12, fontWeight: 600, color: '#B91C1C', cursor: 'pointer',
  },
  droppedList: {
    margin: '8px 0 0', paddingLeft: 16, fontSize: 11,
    color: '#7F1D1D', maxHeight: 180, overflowY: 'auto',
  },
  droppedItem: { marginBottom: 4, lineHeight: 1.4 },
  droppedCategory: {
    fontFamily: 'ui-monospace, monospace', fontWeight: 600,
    fontSize: 10, color: '#991B1B',
  },
  droppedPath: { color: '#9F7AEA', fontSize: 10 },
};
